import { PickingInfo } from '@deck.gl/core';
import { IconLayer } from '@deck.gl/layers';
import { GeofenceType } from '@treadinc/horizon-api-spec';
import { useCallback, useMemo } from 'react';

import {
  ACTIVE_SITE_ICON_SIZE,
  DEFAULT_SITE_ICON_SIZE,
  SITE_ICON_ATLAS,
  SITE_ICON_MAPPING,
  SiteIconType,
} from '~components/Maps/v2/siteIconAtlas';
import { RGBAColor } from '~components/Maps/v2/utils/transformSiteData';
import { Geofence, Site, SiteBasic } from '~hooks/useSites/models';
import { Nullable } from '~types/Nullable';

/**
 * Data structure for a single site marker in the DeckGL IconLayer.
 * Adapted for Sites Settings page (no waypoint context).
 */
interface SitesMapLayerDatum {
  id: string;
  position: [number, number];
  icon: SiteIconType;
  site: Site;
}

/**
 * Map bounds for auto-fitting the view.
 */
interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/**
 * Circle geofence data for Sites Settings map.
 */
export interface SitesCircleGeofenceDatum {
  id: string;
  siteId: string;
  siteName: string;
  site: SiteBasic;
  center: [number, number];
  radiusMeters: number;
  fillColor: RGBAColor;
  strokeColor: RGBAColor;
  isPrimary: boolean;
  tag?: string;
  isMovingSite: boolean;
}

/**
 * Polygon geofence data for Sites Settings map.
 */
export interface SitesPolygonGeofenceDatum {
  id: string;
  siteId: string;
  siteName: string;
  site: SiteBasic;
  coordinates: number[][][];
  fillColor: RGBAColor;
  strokeColor: RGBAColor;
  isPrimary: boolean;
  tag?: string;
}

/**
 * Geofence color palette for the sites map.
 */
const GEOFENCE_COLORS = {
  primaryFill: [0, 0, 255, 64] as RGBAColor,
  primaryStroke: [0, 0, 255, 255] as RGBAColor,
  loadingFill: [0, 161, 26, 64] as RGBAColor,
  loadingStroke: [0, 161, 26, 255] as RGBAColor,
  unloadingFill: [255, 122, 0, 64] as RGBAColor,
  unloadingStroke: [255, 122, 0, 255] as RGBAColor,
  checkInFill: [33, 150, 243, 64] as RGBAColor,
  checkInStroke: [33, 150, 243, 255] as RGBAColor,
  scalingFill: [236, 198, 0, 64] as RGBAColor,
  scalingStroke: [236, 198, 0, 255] as RGBAColor,
  ticketingFill: [234, 18, 0, 64] as RGBAColor,
  ticketingStroke: [234, 18, 0, 255] as RGBAColor,
  fallbackFill: [105, 121, 130, 64] as RGBAColor,
  fallbackStroke: [105, 121, 130, 255] as RGBAColor,
} as const;

/**
 * Gets geofence colors based on tag.
 */
function getGeofenceTagColors(tag: Nullable<string>): {
  fill: RGBAColor;
  stroke: RGBAColor;
} {
  switch (tag?.toLowerCase()) {
    case 'loading':
      return { fill: GEOFENCE_COLORS.loadingFill, stroke: GEOFENCE_COLORS.loadingStroke };
    case 'unloading':
      return {
        fill: GEOFENCE_COLORS.unloadingFill,
        stroke: GEOFENCE_COLORS.unloadingStroke,
      };
    case 'check_in':
      return { fill: GEOFENCE_COLORS.checkInFill, stroke: GEOFENCE_COLORS.checkInStroke };
    case 'scaling':
      return { fill: GEOFENCE_COLORS.scalingFill, stroke: GEOFENCE_COLORS.scalingStroke };
    case 'ticketing':
      return {
        fill: GEOFENCE_COLORS.ticketingFill,
        stroke: GEOFENCE_COLORS.ticketingStroke,
      };
    default:
      if (!tag) {
        return {
          fill: GEOFENCE_COLORS.primaryFill,
          stroke: GEOFENCE_COLORS.primaryStroke,
        };
      }
      return {
        fill: GEOFENCE_COLORS.fallbackFill,
        stroke: GEOFENCE_COLORS.fallbackStroke,
      };
  }
}

/**
 * Returns a neutral site icon type for all sites.
 * In Settings, sites don't have the pickup/dropoff context that exists in dispatch,
 * so we use a single neutral icon ('both_site') for all sites.
 */
function getSiteIconType(isActive: boolean): SiteIconType {
  return isActive ? 'both_site_active' : 'both_site';
}

/**
 * Validates that coordinates are valid numbers and not (0, 0).
 * Exported for use in sidebar filtering.
 */
export function isValidCoordinates(
  lat: Nullable<number>,
  lon: Nullable<number>,
): boolean {
  return (
    lat !== null &&
    lon !== null &&
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    !(lat === 0 && lon === 0)
  );
}

interface UseSitesMapDataOptions {
  activeMarkerId?: string | null;
}

interface UseSitesMapDataResult {
  siteLayerData: SitesMapLayerDatum[];
  mapBounds: MapBounds | null;
  getSiteById: (siteId: string) => Site | undefined;
}

/**
 * Hook to transform sites into DeckGL layer data for the Sites Settings map view.
 */
export function useSitesMapData(
  sites: Site[],
  options: UseSitesMapDataOptions = {},
): UseSitesMapDataResult {
  const { activeMarkerId } = options;

  const siteLayerData = useMemo(() => {
    return sites
      .filter((site) => {
        // Filter out sites without valid coordinates
        return isValidCoordinates(site.lat, site.lon);
      })
      .filter((site) => {
        // Filter out moving sites (equipment-based)
        return !site.movingSite;
      })
      .map((site): SitesMapLayerDatum => {
        const lat = site.lat!;
        const lon = site.lon!;
        const isActive = site.id === activeMarkerId;

        return {
          id: site.id,
          position: [lon, lat],
          icon: getSiteIconType(isActive),
          site,
        };
      });
  }, [sites, activeMarkerId]);

  const mapBounds = useMemo(() => {
    if (siteLayerData.length === 0) {
      return null;
    }

    let north = -Infinity;
    let south = Infinity;
    let east = -Infinity;
    let west = Infinity;

    siteLayerData.forEach((datum) => {
      const [lon, lat] = datum.position;
      if (lat > north) north = lat;
      if (lat < south) south = lat;
      if (lon > east) east = lon;
      if (lon < west) west = lon;
    });

    // Add padding to bounds (roughly 10% on each side)
    const latPadding = (north - south) * 0.1 || 0.01;
    const lonPadding = (east - west) * 0.1 || 0.01;

    return {
      north: north + latPadding,
      south: south - latPadding,
      east: east + lonPadding,
      west: west - lonPadding,
    };
  }, [siteLayerData]);

  const getSiteById = useMemo(() => {
    const siteMap = new Map(sites.map((site) => [site.id, site]));
    return (siteId: string) => siteMap.get(siteId);
  }, [sites]);

  return {
    siteLayerData,
    mapBounds,
    getSiteById,
  };
}

/**
 * Pick info for site layer clicks.
 */
export interface SitePickInfo {
  site: SitesMapLayerDatum;
  x: number;
  y: number;
}

interface UseSitesIconLayerOptions {
  onClick?: (info: SitePickInfo) => void;
  onHover?: (info: SitePickInfo | null) => void;
  activeMarkerId?: string | null;
}

/**
 * Custom hook to create a DeckGL IconLayer for site pins in the Sites Settings map.
 */
export function useSitesIconLayer(
  siteLayerData: SitesMapLayerDatum[],
  options: UseSitesIconLayerOptions = {},
): IconLayer<SitesMapLayerDatum>[] {
  const { onClick, onHover, activeMarkerId } = options;

  const handleClick = useCallback(
    (info: PickingInfo<SitesMapLayerDatum>) => {
      if (onClick && info.object) {
        onClick({
          site: info.object,
          x: info.x,
          y: info.y,
        });
      }
    },
    [onClick],
  );

  const handleHover = useCallback(
    (info: PickingInfo<SitesMapLayerDatum>) => {
      if (onHover) {
        if (info.object) {
          onHover({
            site: info.object,
            x: info.x,
            y: info.y,
          });
        } else {
          onHover(null);
        }
      }
    },
    [onHover],
  );

  const layer = useMemo(() => {
    if (siteLayerData.length === 0) {
      return null;
    }

    return new IconLayer<SitesMapLayerDatum>({
      id: 'sites-settings-icon-layer',
      data: siteLayerData,
      iconAtlas: SITE_ICON_ATLAS,
      iconMapping: SITE_ICON_MAPPING,
      getPosition: (d) => d.position,
      getIcon: (d) => d.icon,
      getSize: (d) =>
        d.id === activeMarkerId ? ACTIVE_SITE_ICON_SIZE : DEFAULT_SITE_ICON_SIZE,
      sizeUnits: 'pixels',
      sizeScale: 1,
      sizeMinPixels: 16,
      sizeMaxPixels: 48,
      billboard: true,
      pickable: true,
      onHover: handleHover,
      onClick: handleClick,
      updateTriggers: {
        getSize: activeMarkerId,
        getIcon: activeMarkerId,
      },
    });
  }, [siteLayerData, activeMarkerId, handleClick, handleHover]);

  return layer ? [layer] : [];
}

interface GeofenceLayerData {
  circleGeofences: SitesCircleGeofenceDatum[];
  polygonGeofences: SitesPolygonGeofenceDatum[];
}

/**
 * Extracts primary geofences from ALL sites.
 * Used to display primary geofences on the map without requiring a site selection.
 * Skips moving sites and sites with invalid coordinates.
 */
export function extractAllPrimaryGeofences(sites: Site[]): GeofenceLayerData {
  const circleGeofences: SitesCircleGeofenceDatum[] = [];
  const polygonGeofences: SitesPolygonGeofenceDatum[] = [];

  for (const site of sites) {
    // Skip moving sites (equipment-based)
    if (site.movingSite) {
      continue;
    }

    const primaryGeofence = site.nextBillionGeofence;
    if (!primaryGeofence) {
      continue;
    }

    if (primaryGeofence.geofenceType === GeofenceType.CIRCLE) {
      const centerLat = primaryGeofence.circleCenter?.lat ?? site.lat;
      const centerLon = primaryGeofence.circleCenter?.lon ?? site.lon;

      if (isValidCoordinates(centerLat, centerLon) && primaryGeofence.circleRadius > 0) {
        const colors = getGeofenceTagColors(null);

        circleGeofences.push({
          id: `${site.id}-primary`,
          siteId: site.id,
          siteName: site.name,
          site: site as unknown as SiteBasic,
          center: [centerLon!, centerLat!],
          radiusMeters: primaryGeofence.circleRadius,
          fillColor: colors.fill,
          strokeColor: colors.stroke,
          isPrimary: true,
          isMovingSite: false,
        });
      }
    } else if (primaryGeofence.geofenceType === GeofenceType.POLYGON) {
      const rawCoordinates = primaryGeofence.geojson?.coordinates;
      if (rawCoordinates && Array.isArray(rawCoordinates) && rawCoordinates.length > 0) {
        const coordinates = rawCoordinates as unknown as number[][][];
        const firstRing = coordinates[0];
        if (firstRing && Array.isArray(firstRing) && firstRing.length >= 3) {
          const colors = getGeofenceTagColors(null);

          polygonGeofences.push({
            id: `${site.id}-primary`,
            siteId: site.id,
            siteName: site.name,
            site: site as unknown as SiteBasic,
            coordinates,
            fillColor: colors.fill,
            strokeColor: colors.stroke,
            isPrimary: true,
          });
        }
      }
    }
  }

  // Sort by size (larger first, so smaller render on top)
  circleGeofences.sort((a, b) => b.radiusMeters - a.radiusMeters);

  return { circleGeofences, polygonGeofences };
}

/**
 * Extracts ONLY additional geofences for a selected site.
 * Does NOT include the primary geofence (that's handled by extractAllPrimaryGeofences).
 * Used when a site is selected to show its additional geofences.
 */
export function extractSelectedSiteAdditionalGeofences(
  site: Site | null,
  additionalGeofences: Geofence[] = [],
): GeofenceLayerData {
  const circleGeofences: SitesCircleGeofenceDatum[] = [];
  const polygonGeofences: SitesPolygonGeofenceDatum[] = [];

  if (!site || additionalGeofences.length === 0) {
    return { circleGeofences, polygonGeofences };
  }

  additionalGeofences.forEach((geofence, index) => {
    const colors = getGeofenceTagColors(geofence.tag);

    if (geofence.type === 'circle') {
      const centerLat = geofence.circleCenter?.lat ?? site.lat;
      const centerLon = geofence.circleCenter?.lon ?? site.lon;

      if (isValidCoordinates(centerLat, centerLon) && geofence.circleRadius > 0) {
        circleGeofences.push({
          id: `${site.id}-additional-${index}`,
          siteId: site.id,
          siteName: site.name,
          site: site as unknown as SiteBasic,
          center: [centerLon!, centerLat!],
          radiusMeters: geofence.circleRadius,
          fillColor: colors.fill,
          strokeColor: colors.stroke,
          isPrimary: false,
          tag: geofence.tag ?? undefined,
          isMovingSite: false,
        });
      }
    } else if (geofence.type === 'polygon') {
      const rawCoordinates = geofence.geojson?.coordinates;
      if (rawCoordinates && Array.isArray(rawCoordinates) && rawCoordinates.length > 0) {
        const coordinates = rawCoordinates as unknown as number[][][];
        const firstRing = coordinates[0];
        if (firstRing && Array.isArray(firstRing) && firstRing.length >= 3) {
          polygonGeofences.push({
            id: `${site.id}-additional-${index}`,
            siteId: site.id,
            siteName: site.name,
            site: site as unknown as SiteBasic,
            coordinates,
            fillColor: colors.fill,
            strokeColor: colors.stroke,
            isPrimary: false,
            tag: geofence.tag ?? undefined,
          });
        }
      }
    }
  });

  // Sort by size (larger first, so smaller render on top)
  circleGeofences.sort((a, b) => b.radiusMeters - a.radiusMeters);

  return { circleGeofences, polygonGeofences };
}

/**
 * Calculates map bounds that include a site and all its geofences.
 */
export function calculateSiteBounds(
  site: Site,
  additionalGeofences: Geofence[] = [],
): google.maps.LatLngBoundsLiteral | null {
  if (!isValidCoordinates(site.lat, site.lon)) {
    return null;
  }

  let north = site.lat!;
  let south = site.lat!;
  let east = site.lon!;
  let west = site.lon!;

  const expandBounds = (lat: number, lon: number) => {
    if (lat > north) north = lat;
    if (lat < south) south = lat;
    if (lon > east) east = lon;
    if (lon < west) west = lon;
  };

  // Expand for primary circle geofence
  const primaryGeofence = site.nextBillionGeofence;
  if (
    primaryGeofence?.geofenceType === GeofenceType.CIRCLE &&
    primaryGeofence.circleRadius > 0
  ) {
    const centerLat = primaryGeofence.circleCenter?.lat ?? site.lat!;
    const centerLon = primaryGeofence.circleCenter?.lon ?? site.lon!;
    // Approximate degrees per meter at this latitude
    const metersPerDegree = 111320 * Math.cos((centerLat * Math.PI) / 180);
    const radiusDegrees = primaryGeofence.circleRadius / metersPerDegree;
    expandBounds(centerLat + radiusDegrees, centerLon + radiusDegrees);
    expandBounds(centerLat - radiusDegrees, centerLon - radiusDegrees);
  }

  // Expand for primary polygon geofence
  if (primaryGeofence?.geofenceType === GeofenceType.POLYGON) {
    const coords = primaryGeofence.geojson?.coordinates as unknown as number[][][];
    if (coords && Array.isArray(coords) && coords.length > 0) {
      const firstRing = coords[0];
      if (Array.isArray(firstRing)) {
        firstRing.forEach((point) => {
          const [lng, lat] = point as [number, number];
          expandBounds(lat, lng);
        });
      }
    }
  }

  // Expand for additional geofences
  additionalGeofences.forEach((geofence) => {
    if (geofence.type === 'circle' && geofence.circleRadius > 0) {
      const centerLat = geofence.circleCenter?.lat ?? site.lat!;
      const centerLon = geofence.circleCenter?.lon ?? site.lon!;
      const metersPerDegree = 111320 * Math.cos((centerLat * Math.PI) / 180);
      const radiusDegrees = geofence.circleRadius / metersPerDegree;
      expandBounds(centerLat + radiusDegrees, centerLon + radiusDegrees);
      expandBounds(centerLat - radiusDegrees, centerLon - radiusDegrees);
    } else if (geofence.type === 'polygon') {
      const coords = geofence.geojson?.coordinates as unknown as number[][][];
      if (coords && Array.isArray(coords) && coords.length > 0) {
        const firstRing = coords[0];
        if (Array.isArray(firstRing)) {
          firstRing.forEach((point) => {
            const [lng, lat] = point as [number, number];
            expandBounds(lat, lng);
          });
        }
      }
    }
  });

  // Add padding
  const latPadding = (north - south) * 0.2 || 0.001;
  const lonPadding = (east - west) * 0.2 || 0.001;

  return {
    north: north + latPadding,
    south: south - latPadding,
    east: east + lonPadding,
    west: west - lonPadding,
  };
}
